import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
} from '@aaas/shared';
import positionService from '../services/position.service';
import type { Position, PositionCreateInput, PositionUpdateInput, PositionStatus } from '../types';

interface PositionFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  position?: Position | null;
  onSuccess: () => void;
}

export function PositionFormDialog({
  open,
  onClose,
  mode,
  position,
  onSuccess,
}: PositionFormDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<PositionStatus>('active');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or position changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && position) {
        setCode(position.code);
        setName(position.name);
        setDescription(position.description || '');
        setStatus(position.status);
      } else {
        setCode('');
        setName('');
        setDescription('');
        setStatus('active');
      }
      setError(null);
    }
  }, [open, mode, position]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!code.trim()) {
      setError(t('positionConfig.codeLabel'));
      return;
    }
    if (!name.trim()) {
      setError(t('positionConfig.nameLabel'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: PositionCreateInput = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          status,
        };
        await positionService.createPosition(currentWorkspace.id, token, data);
      } else if (position) {
        const data: PositionUpdateInput = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          status,
        };
        await positionService.updatePosition(position.id, currentWorkspace.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} position:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('positionConfig.createTitle')
              : t('positionConfig.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('positionConfig.codeLabel')}</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('positionConfig.codePlaceholder')}
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('positionConfig.statusLabel')}</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as PositionStatus)}>
                <SelectTrigger className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}>
                  <SelectValue placeholder={t('positionConfig.statusPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('positionConfig.statusActive')}</SelectItem>
                  <SelectItem value="inactive">{t('positionConfig.statusInactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('positionConfig.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('positionConfig.namePlaceholderFull')}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('positionConfig.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('positionConfig.descriptionPlaceholder')}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : mode === 'create' ? (
                t('positionConfig.createButton')
              ) : (
                t('positionConfig.updateButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
